import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabase";
import { acquireSharedRealtimeChannel } from "@/lib/sharedRealtimeChannel";
import { hydrateLeftSquads } from "@/lib/leftSquads";
import { fetchMyPendingInvites } from "@/lib/squadInvites";
import { settingsSquadsQueryKey, userSquadsQueryKey } from "@/lib/squad";
import i18n from "@/lib/i18next";

const DEDUPE_MS = 2_000;

type SquadMemberRow = {
  squad_id?: string;
  user_id?: string;
  status?: string;
};

export function useSquadInviteSync() {
  const { user, isGuest, hydrated } = useAuth();
  const queryClient = useQueryClient();
  const handledAtRef = useRef<Map<string, number>>(new Map());
  const userId = user && !isGuest ? user.id : undefined;

  useEffect(() => {
    if (!hydrated || !userId) return;

    const shouldHandle = (squadId: string): boolean => {
      const now = Date.now();
      const last = handledAtRef.current.get(squadId);
      if (last != null && now - last < DEDUPE_MS) return false;
      handledAtRef.current.set(squadId, now);
      return true;
    };

    const handleInvited = async (squadId: string) => {
      if (!shouldHandle(squadId)) return;

      await hydrateLeftSquads(userId);

      const invites = await fetchMyPendingInvites();
      const invite = invites.find((row) => row.squad_id === squadId);
      const squadName = invite?.squad_name ?? i18n.t("consumer:squad.aSquadFallback");

      toast.info(i18n.t("consumer:squad.reinvitedToast", { name: squadName }));

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: userSquadsQueryKey(userId) }),
        queryClient.invalidateQueries({ queryKey: settingsSquadsQueryKey(userId) }),
      ]);
    };

    const onMembershipChange = (row: SquadMemberRow | undefined) => {
      if (!row?.squad_id || row.status !== "invited") return;
      void handleInvited(row.squad_id);
    };

    const topic = `squad-invite-sync-${userId}`;
    return acquireSharedRealtimeChannel(
      topic,
      (broadcast) =>
        supabase
          .channel(topic)
          .on(
            "postgres_changes",
            {
              event: "INSERT",
              schema: "public",
              table: "squad_members",
              filter: `user_id=eq.${userId}`,
            },
            (payload) => {
              onMembershipChange(payload.new as SquadMemberRow);
              broadcast();
            },
          )
          .on(
            "postgres_changes",
            {
              event: "UPDATE",
              schema: "public",
              table: "squad_members",
              filter: `user_id=eq.${userId}`,
            },
            (payload) => {
              onMembershipChange(payload.new as SquadMemberRow);
              broadcast();
            },
          )
          .subscribe(),
      () => {
        /* Primary work runs in channel handlers. */
      },
    );
  }, [hydrated, userId, queryClient]);
}
